import { Link } from "@/lib/router-compat";
import { Fragment } from "react";
import { resolveInternalHref } from "@/lib/seo/internalHref";

import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { dedupeCrumbs } from "@/lib/breadcrumbs";
import { uiText } from "@/lib/i18n/uiText";

export interface Crumb {
  name: string;
  path: string;
}

/**
 * Visible breadcrumb trail that mirrors the `breadcrumbJsonLd` data fed to
 * `useSEO`, so the on-page navigation matches the structured data crawlers see.
 * The last crumb renders as the current page (no link).
 */
export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  const crumbs = dedupeCrumbs(items);
  if (!crumbs.length) return null;
  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        {crumbs.map((item, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <Fragment key={item.path}>
              <BreadcrumbItem className="min-w-0">
                {isLast ? (
                  <BreadcrumbPage className="block max-w-[60vw] truncate sm:max-w-none">
                    {uiText(item.name)}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={resolveInternalHref(item.path)} className="block max-w-[40vw] truncate sm:max-w-none">
                      {uiText(item.name)}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
